import { Component, OnInit, Input } from '@angular/core';
import { BaseComponentDirective } from '../base/base.component';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { LookupService } from '../services/lookup.service';
import { LoadingService } from '../services/loading.service';
import { EMPTY, Subject } from 'rxjs';
import { tap, catchError, takeUntil, take } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { BankDetails } from './models/bank-details.model';
import { PathFinderService } from '../services/path-finder.service';
import { AnswerValue } from '../purchase/models/answer-value.model';
import { EventsService } from '../services/events.service';
import { Question } from '../user-input/models/question.model';
import { BankVerificationService } from '../services/bank-verification.service';
import { BankVerificationRequest } from './models/bank-verification-request.model';
import { validateAccountDetails } from "uk-modulus-check";
import { BankVerificationConfirmationComponent } from './bank-verification-confirmation/bank-verification-confirmation.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BuyerService } from '../services/buyer.service';
import { ToDoService } from '../services/to-do.service';
import { KeyValuePair } from '../models/key-value-pair.model';

@Component({
  selector: 'app-bank-verification',
  templateUrl: './bank-verification.component.html',
  styleUrls: ['./bank-verification.component.scss']
})

export class BankVerificationComponent extends BaseComponentDirective implements OnInit {
  @Input() group: UntypedFormGroup;
  @Input() quoteStateId: number;
  @Input() updatedAnswers: Array<Question>;
  @Input() questionForm: UntypedFormGroup;

  bankDetails: BankDetails;
  billingDetails: Array<Question> = [];
  loadingError$ = new Subject<boolean>();
  isCorrect: boolean;
  allowToProceed: boolean = true;
  isBankVerificationApiAvailable: boolean;
  needsVerification: boolean;
  loading: boolean;
  accountHolderValue: string | AnswerValue;
  bankVerificationService: UntypedFormGroup;
  sortCode: UntypedFormGroup;
  accountNumber: UntypedFormGroup;
  accountHolderName: UntypedFormGroup;
  rollNumber: UntypedFormGroup;
  modulusCheckResult: UntypedFormGroup;
  modulusCheckOverridden: UntypedFormGroup;
  isValidateError: boolean = false;
  boxChecked: boolean = false;
  showBankResponse: boolean;
  hasChanged: boolean = false;
  isModulusCheckPreemptive: boolean = false;

  constructor(private lookupService: LookupService,
    private notifications: NotificationService,
    private pathFinder: PathFinderService,
    private loadingService: LoadingService,
    private eventsService: EventsService,
    private bankService: BankVerificationService,
    private modalService: NgbModal,
    private buyerService: BuyerService,
    private todoService: ToDoService) {
    super();
  }

  async ngOnInit() {
    this.loading = false;
    this.loadingService.loading.emit(this.loading);
    this.needsVerification = true;
    this.createBankDetailsFormGroup();
    this.eventsService.valueChanged.subscribe(() => {
      this.detailsChanged();
    });
    this.checkSaveAndQuit();
    this.showBankResponse = await this.shouldShowBankResponse();
  }

  getAnswer(param: string): string {
    return this.pathFinder.getAnswerValue(param, this.updatedAnswers);
  }

  eraseCurrentDetails() {
    this.sortCode.patchValue({ value: null });
    this.accountNumber.patchValue({ value: null });
    this.rollNumber.patchValue({ value: null });
    this.accountHolderName.patchValue({ value: null });
    this.modulusCheckResult.patchValue({ value: null });
    this.modulusCheckOverridden.patchValue({ value: null });
  }

  private isSettingEnabled(name: string, settings: KeyValuePair[]): boolean {
    const setting = settings.find(x => x.key === name);
    if (setting) {
      return setting.value.toLowerCase() === 'true';
    }
    return false;
  }

  verifyBankDetails() {
    this.loading = true;
    this.hasChanged = false;
    this.group.controls.value.setValue(null);

    if (!this.sortCode.controls.value.valid || !this.accountNumber.controls.value.valid || !this.accountHolderName.controls.value.valid) {
      this.allowToProceed = false;
      this.needsVerification = false;
      this.isValidateError = false;
      this.loading = false;
      return;
    }

    this.lookupService.getSettings$('BankVerification').pipe(
      tap(result => {
        if (this.isSettingEnabled('IsBankVerificationApiAvailable', result)) {
          this.isModulusCheckPreemptive = this.isSettingEnabled('PreemptiveModulusCheck', result);
          this.bankService.isBankVerificationServiceAvailable.emit(true);
          if (this.isModulusCheckPreemptive) {
            this.performModulusCheck();
          } else {
            this.callBankVerificationApi();
          }
          
        } else {
          // Hsbc api is down, fall back to uk-modulus-check
          this.bankService.isBankVerificationServiceAvailable.emit(false);
          this.isBankVerificationApiAvailable = false;
          this.needsVerification = false;
          this.performModulusCheck();
        }
      }),
      catchError(err => {
        this.notifications.dangerToast('An error occurred while checking bank verification service availability.', err);
        this.loading = false;
        return EMPTY;
      })
    ).subscribe(() => {
    })
  }
  private performModulusCheck() {
    const validateBankCheck = validateAccountDetails(this.sortCode.controls.value.value, this.accountNumber.controls.value.value);
    this.isValidateError = !validateBankCheck;
    this.isCorrect = validateBankCheck;
    this.modulusCheckResult.patchValue({ value: validateBankCheck });

    if (validateBankCheck) {
      if (this.isModulusCheckPreemptive) {
        this.callBankVerificationApi();
      } else {
        this.allowToProceed = true;
        this.needsVerification = false;
        this.loading = false;
        this.loadingService.loading.emit(this.loading);
        this.group.value.value = "";
        this.group.controls.value.setValue(true);
      }
    } else {
      this.allowToProceed = true;
      this.needsVerification = false;
      if (!this.isModulusCheckPreemptive) {
        this.loading = false;
        this.loadingService.loading.emit(this.loading);
      }
    }
  }

  private callBankVerificationApi() {
    const request = new BankVerificationRequest();
    request.accountNumber = this.accountNumber.controls.value.value.toString();
    request.sortCode = this.sortCode.controls.value.value.toString();
    request.quoteStateId = this.quoteStateId.toString();
    request.creditorName = this.accountHolderName.value.value;

    this.lookupService.verifyBankDetails$(request).pipe(
      tap(details => {
        this.notifications.clearToast();
        this.bankDetails = details;
        if (this.showBankResponse) {
          this.handleBankResponse();
        } else {
          this.allowToProceed = this.bankDetails.allowToProceed;
          if (this.allowToProceed) {
            this.group.controls.value.setValue(true);
            this.needsVerification = false;
            this.loading = false;
            this.loadingService.loading.emit(this.loading);
          } else {
            this.group.controls.value.setValue(null);
            this.needsVerification = false;
            this.loading = false;
            this.loadingService.loading.emit(this.loading);
          }
        }
      }),
      catchError(err => {
        this.loading = false;
        this.loadingError$.next(true);

        if (err.status === 502) {
          // HSBC API error - allow to proceed
          this.allowToProceed = true;
          this.group.controls.value.setValue(true);
          const error502 = new Error('502');
          this.notifications.dangerToast('HSBC API error occurred.', error502);
          return EMPTY;
        }

        // Create a proper error object with meaningful message for other errors
        const errorMessage = err.error?.message || err.error?.Message || err.statusText || 'Unknown error occurred';
        const errorObj = new Error(errorMessage);
        this.notifications.dangerToast('An error occurred while trying to verify bank details.', errorObj);
        return EMPTY;
      }), takeUntil(this.componentDestroyed)
    ).subscribe();
  }

  async shouldShowBankResponse(): Promise<boolean> {
    const buyer = await this.buyerService.getCurrentBuyer();
    const region = buyer.regionId.toString();
    let isRegionInList = false;
    const regionSetting = this.pathFinder.getPurchaseSetting('DisplayBankResponseRegions').toString();
    if (regionSetting === 'all') {
      isRegionInList = true;
    } else {
      regionSetting.split(',').forEach(id => {
        if (region === id) {
          isRegionInList = true;
        }
      });
    }
    return isRegionInList;
  }

  handleBankResponse() {
    const modalRef = this.modalService.open(BankVerificationConfirmationComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.bankDetails = this.bankDetails;
    modalRef.result.then(value => {
      if (value.updatedName) {
        this.accountHolderName.patchValue({ value: value.updatedName });
        this.todoService.saveStatelessAnswer$(this.quoteStateId, 'BankAccountCreditorNameMatch', 'MATCH', false).pipe(
          tap(() => {
          }),
          catchError(() => {
            return EMPTY;
          }),
          take(1)
        ).subscribe();
      }
      if (value.continuePurchase) {
        this.allowToProceed = true;
        this.group.controls.value.setValue(true);
        this.eventsService.performContinuePurchase.emit(true);
      } else {
        this.loading = false;
      }
    }, () => {
      this.loading = false;
    });
  }

  onCheckBox() {
    this.boxChecked = !this.boxChecked;
    if (this.boxChecked) {
      this.modulusCheckOverridden.patchValue({ value: 1 });
      if (this.isModulusCheckPreemptive) {
        this.callBankVerificationApi();
      } else {
        this.group.controls.value.setValue(true);
      }
    } else {
      this.modulusCheckOverridden.patchValue({ value: 0 });
      this.group.controls.value.setValue(null);
    }
  }

  createBankDetailsFormGroup() {
    if (this.questionForm) {
      const controlArray = this.questionForm.get('items') as UntypedFormArray;
      this.bankVerificationService = controlArray.controls[0] as UntypedFormGroup;
      this.sortCode = controlArray.controls[1] as UntypedFormGroup;
      this.accountNumber = controlArray.controls[2] as UntypedFormGroup;
      this.rollNumber = controlArray.controls[3] as UntypedFormGroup;
      this.accountHolderName = controlArray.controls[4] as UntypedFormGroup;
      this.modulusCheckResult = controlArray.controls[6] as UntypedFormGroup;
      this.modulusCheckOverridden = controlArray.controls[7] as UntypedFormGroup;
    }
    this.loading = false;
    this.loadingService.loading.emit(this.loading);
  }

  registerTransitionEvent() {
    this.pathFinder.registerTransitionEvent((onComplete => {
      if (this.allowToProceed) {
        onComplete(true);
      } else {
        // make text red + message
        onComplete(false);
      }
    }));
  }

  get verifyBankDetailsDisabled() {
    if (this.group && this.isBankVerificationApiAvailable === false) {
      return true;
    }
    if (this.needsVerification) {
      return false;
    }
    return true;
  }

  detailsChanged() {
    this.group.patchValue({ value: null });
    this.needsVerification = true;
    this.billingDetails = [];
    this.hasChanged = true;
  }

  checkSaveAndQuit() {
    if (this.getAnswer('BankName') === '' || this.getAnswer('BankName') === 'No service') {
      this.eraseCurrentDetails();
    }
  }

  get fieldsValid(): boolean {
    if (this.accountNumber && this.sortCode && this.accountHolderName)
    {
      return this.accountNumber.controls.value.valid &&
        this.sortCode.controls.value.valid && this.accountHolderName.controls.value.valid;
    }
    return false;
  }
}
